interface SearchableProject {
  project_id: string;
  project_name: string;
}

interface SearchableProjectTask {
  project_id: string;
  status?: string;
  subtask_summary?: {
    items?: Array<{
      content_items?: Array<{
        assignees: string[];
        completed: boolean;
      }>;
    }>;
  };
}

export interface ProjectOverviewSearchResult<T> {
  matchedAssigneeId?: string;
  project: T;
}

function normalizeSearchText(value: string) {
  return value.trim().toLocaleLowerCase("zh-CN");
}

function getProjectActiveAssignees(
  tasks: SearchableProjectTask[],
  canSearchAssignee: (assigneeId: string) => boolean
) {
  const result = new Map<string, Set<string>>();
  tasks.forEach((task) => {
    if (task.status === "discarded") {
      return;
    }
    const assigneeIds = (task.subtask_summary?.items || []).flatMap((subtask) =>
      (subtask.content_items || [])
        .filter((contentItem) => !contentItem.completed)
        .flatMap((contentItem) => contentItem.assignees)
        .filter((assigneeId) => assigneeId && canSearchAssignee(assigneeId))
    );
    if (!assigneeIds.length) {
      return;
    }
    const projectAssignees = result.get(task.project_id) || new Set<string>();
    assigneeIds.forEach((assigneeId) => projectAssignees.add(assigneeId));
    result.set(task.project_id, projectAssignees);
  });
  return result;
}

export function searchProjectOverviewProjects<T extends SearchableProject>(
  projects: T[],
  tasks: SearchableProjectTask[],
  keyword: string,
  getMemberDisplayName: (username: string) => string,
  canSearchAssignee: (assigneeId: string) => boolean = () => true
): ProjectOverviewSearchResult<T>[] {
  const normalizedKeyword = normalizeSearchText(keyword);
  if (!normalizedKeyword) {
    return projects.map((project) => ({ project }));
  }

  const projectAssigneeMap = getProjectActiveAssignees(
    tasks,
    canSearchAssignee
  );
  const allAssigneeIds = Array.from(
    new Set(
      Array.from(projectAssigneeMap.values()).flatMap((assignees) =>
        Array.from(assignees)
      )
    )
  );
  const exactAssigneeIds = allAssigneeIds.filter((assigneeId) => {
    return (
      normalizeSearchText(assigneeId) === normalizedKeyword ||
      normalizeSearchText(getMemberDisplayName(assigneeId)) === normalizedKeyword
    );
  });
  const matchingAssigneeIds = exactAssigneeIds.length
    ? exactAssigneeIds
    : allAssigneeIds.filter((assigneeId) =>
        [assigneeId, getMemberDisplayName(assigneeId)].some((value) =>
          normalizeSearchText(value).includes(normalizedKeyword)
        )
      );

  return projects.flatMap((project) => {
    const projectAssignees = projectAssigneeMap.get(project.project_id);
    const matchedAssigneeId = matchingAssigneeIds.find((assigneeId) =>
      projectAssignees?.has(assigneeId)
    );
    if (matchedAssigneeId) {
      return [{ project, matchedAssigneeId }];
    }
    if (
      !exactAssigneeIds.length &&
      normalizeSearchText(project.project_name).includes(normalizedKeyword)
    ) {
      return [{ project }];
    }
    return [];
  });
}
